import { useCallback } from "react";
import { Image } from "../../../../components/common/image";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import type { SaleDto } from "../../../../api";
import {
  deriveQuotationStatus,
  formatCreatedAt,
  getExpiryInfo,
} from "../../utils/quotation-status";
import { useSaleDetails } from "../../../sales/hooks/useSaleDetails";

export interface QuotationRowProps {
  sale: SaleDto;
  itemCount: number;
  onView: (id: number) => void;
  onPay: (id: number) => void;
  onRenew: (id: number) => void;
  isRenewing: boolean;
}

export function QuotationRow({
  sale,
  itemCount,
  onView,
  onPay,
  onRenew,
  isRenewing,
}: QuotationRowProps) {
  const { data: details } = useSaleDetails(sale.id);
  const status = deriveQuotationStatus(sale.state, sale.validityDate);
  const expiry = getExpiryInfo(sale.validityDate, sale.createDate);
  const description = `${itemCount} item${itemCount === 1 ? "" : "s"}`;
  const firstImage = details?.orderLines?.find((line) => line.image)?.image ?? null;

  const handleRowClick = useCallback(() => onView(sale.id), [onView, sale.id]);
  const handleStop = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  const isExpired = status === "expired";
  const isPaid = status === "paid";
  const isCancelled = status === "cancelled";

  return (
    <div
      className="flex cursor-pointer items-center gap-6 rounded-xl border border-borderDefault bg-bgWhite px-6 py-4 transition-colors max-md:flex-wrap max-md:gap-x-4 max-md:gap-y-3 max-md:px-4 hover:border-borderDark"
      onClick={handleRowClick}
    >
      <div className="flex w-[44%] min-w-0 items-center gap-3 max-md:w-full">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-bgLight">
          {firstImage ? (
            <Image
              image={firstImage}
              alt={sale.name}
              size="w-full h-full object-cover"
            />
          ) : null}
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <Text variant="bodyBold" tone="dark" className="truncate">
            {sale.name}
          </Text>
          <Text variant="bodySm" tone="muted">{description}</Text>
          <Text variant="bodyXs" tone="muted">{formatCreatedAt(sale.createDate)}</Text>
        </div>
      </div>

      <div className="flex w-28 shrink-0 flex-col">
        <Text
          variant="bodyBold"
          tone="inherit"
          inline
          className={expiry.isUrgent ? "text-accentError" : "text-textDark"}
        >
          {expiry.label ?? "-"}
        </Text>
        <Text variant="bodyXs" tone="muted">
          {isExpired ? "Expired" : isPaid ? "Paid" : isCancelled ? "Cancelled" : "Expires in"}
        </Text>
      </div>

      <div className="flex w-32 shrink-0 flex-col">
        <CurrencyAmount
          amount={sale.amountTotal}
          currency={sale.currency?.name ?? "QAR"}
          variant="bodyBold"
          tone="dark"
        />
        <Text variant="bodyXs" tone="muted">Excl. VAT</Text>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2 max-md:ml-0 max-md:w-full" onClick={handleStop}>
        {isExpired ? (
          <Button
            variant="secondary"
            size="sm"
            className="max-md:flex-1"
            onClick={() => onRenew(sale.id)}
            disabled={isRenewing}
          >
            {isRenewing ? "Renewing…" : "Renew"}
          </Button>
        ) : isPaid || isCancelled ? (
          <Button variant="secondary" size="sm" className="px-6 max-md:flex-1" onClick={handleRowClick}>
            View
          </Button>
        ) : (
          <>
            <Button variant="primary" size="sm" className="px-6 max-md:flex-1" onClick={() => onPay(sale.id)}>
              Buy Now
            </Button>
            <Button variant="secondary" size="sm" className="px-6 max-md:flex-1" onClick={handleRowClick}>
              View
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
